import type { Command } from '@tunasong/plugin-lib'
import { ListItemIcon, ListItemText, MenuItem, Submenu, SubmenuContent, SubmenuTrigger } from '@tunasong/ui-lib'
import type { FC, MouseEvent } from 'react'

export interface EntityCommandMenuItemsProps {
  /** Entity commands, already filtered (streaming commands removed) and ordered. */
  commands: Command[]
  /** Returns the click handler for a command. The host owns prompt/run/close lifecycle. */
  getHandler: (command: Command) => (ev: MouseEvent) => void
}

/**
 * A contiguous run of commands that share a `groupId`. Built by `groupCommands` in
 * first-seen order; a run of one command is still a group but renders flat.
 */
interface CommandGroup {
  groupId?: string
  commands: Command[]
}

/**
 * Bucket commands by their explicit `groupId` (added in #1483) in first-seen order.
 * Commands without a `groupId` each become their own singleton bucket so they render
 * flat exactly as before. Grouping is generic — nothing agent-specific lives here; we
 * NEVER parse command ids (affordance ids are keyed by runtime agent id, not the
 * definition id, so prefix-parsing silently fails).
 */
const groupCommands = (commands: Command[]): CommandGroup[] => {
  const groups: CommandGroup[] = []
  const byId = new Map<string, CommandGroup>()

  for (const command of commands) {
    if (!command.groupId) {
      groups.push({ commands: [command] })
      continue
    }
    const existing = byId.get(command.groupId)
    if (existing) {
      existing.commands.push(command)
      continue
    }
    const group: CommandGroup = { groupId: command.groupId, commands: [command] }
    byId.set(command.groupId, group)
    groups.push(group)
  }

  return groups
}

const FlatCommandItem: FC<{
  command: Command
  getHandler: EntityCommandMenuItemsProps['getHandler']
  label: string
}> = ({ command, getHandler, label }) => (
  <MenuItem disabled={!command.cmd} onClick={getHandler(command)}>
    {command.Icon ? (
      <ListItemIcon>
        <command.Icon />
      </ListItemIcon>
    ) : null}
    <ListItemText primary={label} />
  </MenuItem>
)

/**
 * Renders entity commands for the context menu: each multi-command agent group collapses
 * into a single flyout `<Submenu>`; single-command groups and ungrouped commands stay flat.
 */
export const EntityCommandMenuItems: FC<EntityCommandMenuItemsProps> = ({ commands, getHandler }) => {
  const groups = groupCommands(commands)

  return (
    <>
      {groups.map(group => {
        // A real group (>= 2 commands sharing a groupId) collapses into a flyout submenu.
        if (group.groupId && group.commands.length >= 2) {
          const GroupIcon = group.commands[0]?.groupIcon
          const groupLabel = group.commands[0]?.groupLabel ?? group.groupId
          return (
            <Submenu key={`group-${group.groupId}`}>
              <SubmenuTrigger>
                {GroupIcon ? (
                  <ListItemIcon>
                    <GroupIcon />
                  </ListItemIcon>
                ) : null}
                <ListItemText primary={groupLabel} />
              </SubmenuTrigger>
              <SubmenuContent aria-label={groupLabel}>
                {group.commands.map(command => (
                  <FlatCommandItem
                    key={command.id}
                    command={command}
                    getHandler={getHandler}
                    label={command.shortName ?? command.name}
                  />
                ))}
              </SubmenuContent>
            </Submenu>
          )
        }

        // Single-command group or ungrouped command: flat with the full `name`.
        const command = group.commands[0]
        return <FlatCommandItem key={command.id} command={command} getHandler={getHandler} label={command.name} />
      })}
    </>
  )
}
